import React, { useEffect, useRef, useState}  from 'react';

//interfaces
import { IHttpRequest } from  '@/interfaces/IHttp';
import { IExistingItem, IOption } from  '@/interfaces/insightsp2';
import { container, TYPES } from  '@/inversifyconfig/container.config';

//hooks
import useInfiniteScroll from  '@/components/hooks/useInfiniteScroll';
import { BuildPath, CmsPath } from  '@/helpers/Paths';
import { Truncate } from  '@/helpers/Text';

//components
import Loading from  '@/components/elements/global/LoadingDots';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import CustomSelect from  '@/components/elements/insights/CustomSelect';
import ThriveCheckBox from  '@/components/elements/global/Checkbox';
import SimpleBar from 'simplebar-react';

//styling
import  '@/css/existingContentModal.scss';



type props = {
    restoreItemId?: number;
    setContentItem?: (item:IExistingItem) => void, 
    setContentItems?: (items: IExistingItem[]) => void;
    close:() => void,
    selectedWorkSpace:IOption,
    buttonText?:string
    selectedContentItems?: IExistingItem[]
    isMultiSelect?: boolean
    showFilterBy?: boolean
    isInsights?: boolean
}

export const orderOptions:IOption[] = [
    { value: 'DateModifiedNewest', label: 'Newest' },
    { value: 'DateModifiedOldest', label: 'Oldest' },
    { value: 'AllContentAZ', label: 'A-Z' },
    { value: 'AllContentZA', label: 'Z-A' }
];

export const filterByOptions:IOption[] = [
    { value: '0', label: 'All Content' },
    { value: '1', label: 'Lists' },
    { value: '2', label: 'Pages' },
    { value: '4', label: 'Contacts' },
    { value: '5', label: 'Web' },
    { value: '7', label: 'File' },
    { value: '10', label: 'Audio' },
    { value: '8', label: 'Video' },
    { value: '6', label: 'Image' },
];

const ExistingItemModal = ({restoreItemId, setContentItem, setContentItems, close, selectedWorkSpace, buttonText, selectedContentItems=[], isMultiSelect=false, showFilterBy=false, isInsights=false}: props) => {

    const [order, setOrder] = useState<IOption>(null);
    const [filterBy, setFilterBy] = useState<IOption>();
    const [keyword, setKeyword] = useState<string>("");
    const [selectedItem, setSelectedItem] = useState<IExistingItem>(null);
    const [selectedItems, setSelectedItems] = useState<IExistingItem[]>(selectedContentItems);
    const [existingItems, setExistingItems] = useState<IExistingItem[]>([]);
    const [skip, setSkip] = useState<number>(0);
    const [lastCount, setLastCount] = useState<number>(null);
    const [reset, setReset] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const take = 24;

    // Ref
    const searchResults = useRef(null);
    const orderRef = useRef(null);
    const contentTypeRef = useRef(null);

    // Loading
    const [initLoad, setInitLoad] = useState<boolean>(true);

    // Init
    useEffect(() => {
        search(true, true);

        // Css on/off causing different issue so applying with Js
        document.body.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = 'initial';
        }

    }, []);

    // Reset search
    useEffect(() => {
        if (reset){
            search(true, true);
        }
    }, [reset])

    // Infinite scroll
    useEffect(() => {
        if(skip){
            search(false, true);
        }
    }, [skip]);

    // Search on select change
    useEffect(() => {  
        if (order){
            search(true, true);
        }
    }, [order])

    useEffect(() => {
        if (filterBy){
            search(true, true);
        }
    }, [filterBy])



    const { paginationDiv } = useInfiniteScroll({ 
        querySelector: `search-results`,
        fetchCondition: lastCount === take && !isLoading,
        fetchCb: () => setSkip(skip + take)
    })

    function search(clear:boolean, initSearch:boolean):void {

        // Break if last count less than take
        if (!clear && lastCount != null && lastCount < take){
            return;
        }


        // Clear - new search
        if (clear && searchResults.current) {
            document.querySelector(".simplebar-content-wrapper").scrollTop = 0;
            setSkip(0);
            setLastCount(null);
        }
        setIsLoading(true);

        container.get<IHttpRequest>(TYPES.IHttpRequest).get(`/existingcontent/${isInsights? 'searchinsights' : 'search' }?${queryBuilder(clear, initSearch)}`).then(({data}) => {
            
            // Change order
            moveMainMenuToStart(data.items)

            // New search or init
            if (clear){           
                setExistingItems([...data.items as any]);
            }
            // Paginated
            else{
                setExistingItems(current => [...current, ...data.items as any]);
            }
            
            setLastCount(data.items.length);
           

        }).catch((error) => {
           console.log(`Existing content error: ${error}`);
        }).finally(() => {
            setInitLoad(false);
            setReset(false);
            setIsLoading(false);
        });
    }

    function moveMainMenuToStart(items:any){

        items.sort((x,y) => { return x.title === "Menu" ? -1 : y === "Menu" ? 1 : 0; })

    }

    function queryBuilder(clear:boolean, initSearch?:boolean):string {
        let query = `skip=${skip}&take=${take}`;

        if (clear){
            query = `skip=0&take=${take}`;
        }

        if (keyword){
            query += `&keyword=${keyword}`;
        }

        if (order){
            query += `&order=${order.value}`;
        }
        else {
            query += `&order=DateModifiedNewest`
        }

        if (buttonText === "Restore"){
            query += '&filterBy=1&includedraft=true';

            if (initSearch){
                query += `&includemainmenu=true`
            }
        }
        else if (filterBy){
            query += `&filterBy=${filterBy.value}`;
        }

        if (selectedWorkSpace){
            query += `&selectedWorkSpace=${selectedWorkSpace.value}`
        }

        if (!isInsights && restoreItemId) {
            query += `&exclude=${restoreItemId}`
        }

        return query;
    }

    function highLight(item:IExistingItem){

        if (selectedItem && selectedItem.id === item.id){
            return "highlighted";
        }

        return "";
    }

    function save(){
        if (selectedItem){
            setContentItem(selectedItem);
            close();
        }
        if(selectedItems.length) {
            setContentItems(selectedItems)
            close();
        }
    }

    function handleKeyDown(event){
        
        if (keyword != "" && event.key === 'Enter') {
            search(true, false);
        }
    }

    function imagePath(item:IExistingItem):string {
        
        if (item.title === "Menu") {
            return `${CmsPath()}/Content/img/widgets/310/List.png?size=Thumbnail`
        }

        if (!item.thumbnail){
            return `${BuildPath()}/images/missingimage.png`
        }

        if (item.thumbnail.includes('http')){
            return item.thumbnail;
        }

        return `${CmsPath()}${item.thumbnail}?size=Thumbnail`;
    }

    function resetSearch(){

        orderRef.current.clear();
        contentTypeRef.current?.clear();

        setOrder(null);
        setFilterBy(null);
        setKeyword("");
        setSelectedItem(null);
        setExistingItems([]);
        setSkip(0);
        setLastCount(null);  
        setReset(true);
    }

    function toggleMultiSelectOption(item: IExistingItem) {
        if(selectedItems.map(item => item.id).includes(item.id)) {
            setSelectedItems(selectedItems.filter(i => i.id !== item.id))
        }
        else {
            if(selectedItems.length >= 10) {
                return
            }
            setSelectedItems([...selectedItems, item])
        }
    }

    function hasSelections(){
        return !selectedItem && !selectedItems.length
    }
    
    return (

        <div className="existing-items-modal insights-container">

                <h2>{buttonText === "Restore" ? 'Select a Location' : 'Existing'}</h2>

                <div className="controls">

                    <div className="select-label react-selector date">

                        <CustomSelect 
                            options={orderOptions} 
                            optionsChange={(item) => setOrder(item[0])} 
                            ref={orderRef} 
                            multi={false} 
                            showCalendar={false} 
                            placeholder="Sort by : Newest"
                            defaultSelectedIndex={0}
                        />

                    </div>

                    {showFilterBy && 
                        <div className="select-label react-selector filterBy">

                            <CustomSelect 
                                options={filterByOptions} 
                                optionsChange={(item) => setFilterBy(item[0])} 
                                ref={contentTypeRef} 
                                multi={false} 
                                showCalendar={false} 
                                placeholder="Show All Content"
                            />

                        </div>
                    }

                    <div className="searchText">
                        {keyword.trim() !== "" && <div className="clearSearch" onClick={resetSearch}>X</div>}
                        <label htmlFor='searchtextinput' className='sr-only'>Search by Title</label>
                        <input id="searchtextinput" type="text" placeholder="Search by Title" value={keyword} onChange={(e) => setKeyword(e.target.value)} onKeyDown={handleKeyDown} className="in-textbox" />
                    </div>

                    <div className="button">
                        <button className="in-button" disabled={keyword === ""} onClick={() => search(true, false)}>Search</button>
                    </div>
                    
                </div>

                {!initLoad && !existingItems.length && <div className="no-results">No content found for this selection</div>}
                {isLoading && <div className='loading-wrapper'><Loading /></div>}
                <SimpleBar className="scroll-results" ref={searchResults} style={{ maxHeight: 365 }} forceVisible={true} autoHide={false} >
                        <ul className="search-results">
                        {isMultiSelect
                            ? <>
                            {existingItems.map((item:IExistingItem) => {
                                return(
                                    <><li className='existing-item' onClick={() => toggleMultiSelectOption(item)} key={item.id} data-tooltip-id={`contentError-${item.id}`}> 
                                        <img src={imagePath(item)} className={highLight(item)} alt=""/>
                                        <span>{Truncate(item.title, 13)}</span>    
                                        <span>{item.contentTypeLabel}</span>
                                        <ThriveCheckBox
                                            id={`${item.id}`}
                                            value={selectedItems.map(item => item.id).includes(item.id)}
                                            name={`${item.title} select`}
                                            change={() =>{}}
                                        />
                                    </li>
                                    {selectedItems.length >= 10 && 
                                        <ReactTooltip id={`contentError-${item.id}`}  className='thrive-tooltip'>
                                            <span className='content-error-tooltip'>You can only compare up to 10 content items at a time</span>
                                        </ReactTooltip>
        }
                                    </>
                                )             
                            })}</>
                            : <>
                            {existingItems.map((item:IExistingItem) => {
                                return(
                                    <li onClick={() => setSelectedItem(item)} key={item.id}> 
                                        <img src={imagePath(item)} className={highLight(item)} alt=""/>
                                        <span>{Truncate(item.title, 13)}</span>    
                                        <span>{item.contentTypeLabel}</span>
                                    </li>
                                )             
                            })}</>
                        }
                        <div style={{height:'20px'}} ref={paginationDiv as any}/>
                    </ul>
                    

                </SimpleBar>

                <div className="save-bar">
                    <button className="in-button" onClick={save} disabled={hasSelections()}>{buttonText ? buttonText :'Add Content Item'}</button>
                    <button className="in-button cancel" onClick={() => close()}>Cancel</button>
                    {isMultiSelect && <span className='item-count'>Showing insights for {selectedItems.length} {selectedItems.length === 1 ? 'item' : 'items'}</span>}
                </div>         

            </div>      
    
    );
};

export default ExistingItemModal;